import { Injectable } from '@angular/core';
import { CanvasProvider } from './workspace/canvas';
import { ActiveToolProvider } from './workspace/active-tool';
import { DatasetProvider } from './dataset';
import { DatasetNavBarProvider, DatasetNavLocation } from './dataset_navbar';
import { AssetProvider, TaggableAsset } from './asset';
import { ModalProvider } from './popup/modal';
import { BehaviorSubject } from 'rxjs';
import { AppNavProvider } from '../providers/app_nav';
import { UserProvider, User } from '../providers/user';
import { TranslateService } from '@ngx-translate/core';

export class LockStatus {
    locked: boolean;
    lockedBy: string;
}

@Injectable()
export class AssetLockProvider {
    public isLockedObservable: BehaviorSubject<LockStatus> = new BehaviorSubject<LockStatus>({
        locked: false,
        lockedBy: ''
    });

    private _lockStatus: LockStatus;

    get lockStatus(): LockStatus {
        return this._lockStatus;
    }

    set lockStatus(status: LockStatus) {
        this._lockStatus = status;
        this.isLockedObservable.next(status);
    }

    public updateLockTimeout: number;
    public expirationWarningTimeout: number;

    public expirationWarningModal;
    public expirationModal;

    // We need to .bind() scope in order to have access to our scope when running timeout logic.
    // However, doing so changes the function signature, which can prevent removing event listeners.
    // To get around this, we need to store a non-anonymous reference to our function.
    private updateIdleTimeoutBound: EventListenerOrEventListenerObject = this.updateIdleTimeout.bind(this);

    private readonly LOCK_RENEWAL_INTERVAL = 55000; // 55 seconds
    private readonly EXPIRATION_WARNING_INTERVAL = 600000; // 10 minutes (600 seconds)

    public asset: TaggableAsset | null;
    public datasetIdentifier: string; // Fetched as necessary when attempting to lock an asset

    constructor(
        private datasetProvider: DatasetProvider,
        private assetProvider: AssetProvider,
        private modalProvider: ModalProvider,
        private canvasProvider: CanvasProvider,
        private activeToolProvider: ActiveToolProvider,
        private appNavProvider: AppNavProvider,
        private userProvider: UserProvider,
        private datasetNavBarProvider: DatasetNavBarProvider,
        private translate: TranslateService
    ) {
        // These listeners allow us to estimate when the user is "working".
        window.addEventListener('click', this.updateIdleTimeoutBound, true);
        window.addEventListener('keydown', this.updateIdleTimeoutBound, true);
        window.addEventListener('mousewheel', this.updateIdleTimeoutBound, true);
    }

    public isAssetLocked(): boolean {
        return this.lockStatus && this.lockStatus.locked;
    }

    public isAssetLockedByDifferentUser(): boolean {
        const currentUser = this.userProvider.getUserInformation();
        return this.isAssetLocked() && this.lockStatus.lockedBy !== currentUser.name;
    }

    public async lockAsset(asset: TaggableAsset): Promise<void> {
        this.lockStatus = {
            locked: false,
            lockedBy: ''
        };

        this.datasetIdentifier = this.datasetProvider.getCurrentDatasetIdentifier();

        this.asset = asset;

        if (this.asset) {
            try {
                const resp = await this.assetProvider.lockAsset(this.asset.id);

                if (resp.acquired) {
                    const currentUser = this.userProvider.getUserInformation();
                    this.lockStatus = {
                        locked: true,
                        lockedBy: currentUser.name
                    };
                    this.updateLockInterval();
                    this.updateIdleTimeout();
                } else {
                    if (resp.lockedByUser) {
                        // If lockedByUser from lockAsset is empty, it means we tried to
                        // lock an asset that we've already locked (instead of renewing the lock).
                        this.lockStatus = {
                            locked: true,
                            lockedBy: resp.lockedByUser.name
                        };
                        this.canvasProvider.setLockState(true);
                        this.activeToolProvider.lock();
                        this.showLockModal(true);
                    } else {
                        // Otherwise, we have the lock, and should renew it instead.
                        // This can happen if the user exits out of Brain Builder, but
                        // returns to an asset before their lock would otherwise expire.
                        this.renewAssetLock(true);
                    }
                }
            } catch (e) {
                throw e;
            }
        }
    }

    public async renewAssetLock(initialLock = false): Promise<void> {
        try {
            if (!this.asset) {
                return;
            }

            const resp = await this.assetProvider.renewAssetLock(this.asset.id);

            if (resp.acquired) {
                this.updateLockInterval();

                // If we're "acquiring" the lock by returning to the asset and
                // renewing the existing lock, that's a good opportunity to set
                // up an idle timeout.
                if (initialLock) {
                    this.updateIdleTimeout();
                }
            } else {
                if (this.updateLockTimeout) {
                    // Clear the timeout;
                    window.clearTimeout(this.updateLockTimeout);
                }

                const currentUser = this.userProvider.getUserInformation();
                if (resp.lockedByUser) {
                    if (resp.lockedByUser.name !== currentUser.name) {
                        this.lockStatus = {
                            locked: true,
                            lockedBy: resp.lockedByUser.name
                        };

                        this.canvasProvider.setLockState(true);
                        this.activeToolProvider.lock();
                        this.showLockModal(true);
                    } else {
                        // If renewing somehow fails, but the lock is supposedly held by the current user,
                        // don't do anything.
                        return;
                    }
                } else {
                    // If lockedByUser is falsey (null), then the asset isn't locked.
                    // We should attempt to reacquire the lock.
                    this.lockAsset(this.asset);
                }
            }
        } catch (e) {
            if (this.updateLockTimeout) {
                window.clearTimeout(this.updateLockTimeout);
            }

            throw e;
        }
    }

    public async unlockCurrentAsset(): Promise<void> {
        if (this.asset) {
            this.lockStatus = {
                locked: false,
                lockedBy: ''
            };
            this.canvasProvider.setLockState(false);
            this.activeToolProvider.unlock();
            await this.assetProvider.unlockAsset(this.asset.id);
        }
    }

    /** Attempt to update the lock interval every 55 seconds.
     * This is called by successfully either acquiring a new lock or renewing an existing one.
     */
    public async updateLockInterval(): Promise<void> {
        // Clean up the original timeout for safety.
        window.clearTimeout(this.updateLockTimeout);

        // NOTE: window.setTimeout() is used to improve typing.
        this.updateLockTimeout = window.setTimeout(() => {
            this.renewAssetLock();
        }, this.LOCK_RENEWAL_INTERVAL);
    }

    public updateIdleTimeout(event?: Event): void {
        // If either expiration modal is up, we don't want input to reset the timers.
        // In that case, we should only reset the timers if the user presses a button.
        if (this.expirationWarningModal || this.expirationModal) {
            return;
        }

        window.clearTimeout(this.expirationWarningTimeout);

        // We want to make absolutely sure that the user does not see these modals unless they're in the workspace.
        if (!this.currentViewIsWorkspace()) {
            this.cleanup();
            return;
        }

        this.expirationWarningTimeout = window.setTimeout(() => {
            // Only allow one warning modal on the screen at a time
            if (!this.expirationWarningModal) {
                this.expirationWarningModal = this.modalProvider.createModal(
                    'AssetLockExpirationWarningModal',
                    {},
                    { enableBackdropDismiss: false, cssClass: 'modal-asset-lock-expiration-warning' }
                );
                this.expirationWarningModal.onDidDismiss(async data => {
                    delete this.expirationWarningModal;
                    if (data && data.isStillWorking) {
                        this.updateIdleTimeout();
                    } else {
                        if (data && data.timeoutExpired) {
                            this.showLockModal(false);
                        } else {
                            this.appNavProvider.getNavController().setRoot('DatasetPage', {
                                datasetIdentifier: this.datasetIdentifier
                            });
                            this.cleanup();
                        }
                    }
                });
                this.expirationWarningModal.present();
            }
        }, this.EXPIRATION_WARNING_INTERVAL);
    }

    public async showLockModal(assetAlreadyLocked: boolean) {
        if (assetAlreadyLocked && this.lockStatus && this.lockStatus.locked) {
            // The user has lost their lock through inactivity; make sure it doesn't linger.
            this.unlockCurrentAsset();
        }

        this.cleanup();

        // Like the warning modal, this should never appear outside the workspace.
        if (!this.currentViewIsWorkspace()) {
            this.cleanup();
            return;
        }

        if (assetAlreadyLocked) {
            await this.modalProvider.presentAlert(
                this.translate.instant('This asset is currently locked.'),
                this.translate.instant(
                    '<p>This asset is currently being worked on by another user in the organization. You will be unable to interact with this asset until they are done.</p><p>Note: If the user is inactive for more than 10 minutes, the system will prompt them to confirm they are still working. If there is no response after 5 minutes, their session will expire, and the asset will be available for use.</p>'
                ),
                this.translate.instant('OK')
            );
        } else {
            await this.modalProvider.presentAlert(
                this.translate.instant('This session has timed out.'),
                this.translate.instant('Please refresh the page to continue working.'),
                this.translate.instant('OK')
            );
        }

        this.appNavProvider.getNavController().setRoot('DatasetPage', {
            datasetIdentifier: this.datasetIdentifier
        });
    }

    private cleanup() {
        if (this.updateLockTimeout) {
            window.clearTimeout(this.updateLockTimeout);
        }

        if (this.expirationWarningTimeout) {
            window.clearTimeout(this.expirationWarningTimeout);
        }
    }

    private currentViewIsWorkspace(): boolean {
        return this.datasetNavBarProvider.getActivePage() === DatasetNavLocation.Workspace;
    }

    // See BB-5689: When navigating from Evaluate page in edit mode, we are trying to acquire a lock
    // on an asset that already holds a lock. By checking if it's locked, we can renew lock
    public async ensureAssetLock(asset: TaggableAsset): Promise<void> {
        if (this.isAssetLocked()) {
            setTimeout(async () => {
                await this.renewAssetLock(true);
            }, 100);
        } else {
            await this.lockAsset(asset);
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/asset_lock.ts